import moment from 'moment';

export const ENTREGAVEL_PCT_OBS_PREFIX = 'ENTREGAVEL_PCT:';
export const ENTREGAVEL_AGENDAMENTO_DATA_FIXA = 'dataFixa';
export const ENTREGAVEL_AGENDAMENTO_PRAZO = 'prazo';

const round2 = (value) => Number(Number(value || 0).toFixed(2));

const parsePercentValue = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number(String(value).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

const parseIntegerValue = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number.parseInt(String(value), 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseMoneyValue = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number(String(value).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

export const calcularValorEntregavel = (valorGlobal, valorPct) => {
  const base = Number(valorGlobal || 0);
  const pct = Number(valorPct || 0);
  if (!base || !pct) return 0;
  return round2((pct / 100) * base);
};

export const calcularPctEntregavel = (valorGlobal, valorVlr) => {
  const base = Number(valorGlobal || 0);
  const vlr = Number(valorVlr || 0);
  if (!base || !vlr) return 0;
  return round2((vlr / base) * 100);
};

const applyValorAndPct = (item, valorPct, valorVlr) => {
  const pct = round2(Number(valorPct || 0));
  const vlr = round2(Number(valorVlr || 0));
  return {
    ...item,
    valorPct: pct,
    contrFornEvento_vlr: vlr,
    contrFornEvento_obs: buildEntregavelObs(pct),
  };
};

export const getValorPctFromEntregavel = (entregavel = {}) => (
  parsePercentValue(entregavel.valorPct)
);

export const getSomaPercentualEntregaveis = (entregaveis = []) => (
  round2((entregaveis || []).reduce((acc, item) => (
    acc + (getValorPctFromEntregavel(item) || 0)
  ), 0))
);

export const buildEntregavelObs = (valorPct) => (
  `${ENTREGAVEL_PCT_OBS_PREFIX}${Number(valorPct || 0)}`
);

export const hasEntregavelDtPrev = (item = {}) => {
  const raw = item.contrFornEvento_dtPrev;
  if (raw == null || raw === '') return false;
  return Boolean(String(raw).trim());
};

export const getEntregavelPrazoDias = (item = {}) => (
  parseIntegerValue(item.contrFornEvento_pgtoPrazoDias)
);

/** true quando o prazo foi informado (inclui 0); false se vazio/ausente. */
export const isPrazoDiasInformado = (value) => {
  if (value == null || value === '') return false;
  const parsed = Number.parseInt(String(value), 10);
  return Number.isFinite(parsed) && parsed >= 0;
};

/**
 * Prefere modo explícito (UI); senão infere por presença de dtPrev.
 * dtPrev preenchido = data já definida (fixa ou já materializada na assinatura).
 * Sem dtPrev + prazo (incl. 0) = modo prazo a materializar na assinatura.
 */
export const getEntregavelAgendamentoMode = (item = {}) => {
  if (
    item.agendamentoMode === ENTREGAVEL_AGENDAMENTO_DATA_FIXA
    || item.agendamentoMode === ENTREGAVEL_AGENDAMENTO_PRAZO
  ) {
    return item.agendamentoMode;
  }
  if (hasEntregavelDtPrev(item)) return ENTREGAVEL_AGENDAMENTO_DATA_FIXA;
  if (isPrazoDiasInformado(item.contrFornEvento_pgtoPrazoDias)) {
    return ENTREGAVEL_AGENDAMENTO_PRAZO;
  }
  return ENTREGAVEL_AGENDAMENTO_PRAZO;
};

/** Persistência na API: inclui 0; omite só quando o prazo não foi informado. */
export const persistPgtoPrazoDias = (value) => (
  isPrazoDiasInformado(value) ? Number.parseInt(String(value), 10) : undefined
);

/** Exibição em tabela: inclui 0; vazio → "Sem Prazo". */
export const formatPgtoPrazoDiasDisplay = (value, emptyLabel = 'Sem Prazo') => (
  isPrazoDiasInformado(value) ? Number.parseInt(String(value), 10) : emptyLabel
);

/**
 * Vencimento do borderô a partir do evento.
 * - Sem dtPrev: indefinido (ainda não materializado).
 * - dependeAtividade + prazo (incl. 0): dtPrev (atividade) + prazo.
 * - Com dtPrev nos demais casos: usa a data prevista (não soma prazo de novo).
 */
export const resolveBdroDtVencFromEvento = (evento = {}) => {
  if (!hasEntregavelDtPrev(evento)) return undefined;
  const base = moment(evento.contrFornEvento_dtPrev);
  if (!base.isValid()) return undefined;
  const dependeAtividade = Number(evento.contrFornEvento_dependeAtividade) === 1;
  if (dependeAtividade && isPrazoDiasInformado(evento.contrFornEvento_pgtoPrazoDias)) {
    return base
      .clone()
      .add(Number.parseInt(String(evento.contrFornEvento_pgtoPrazoDias), 10), 'days')
      .format('YYYY-MM-DD');
  }
  return base.format('YYYY-MM-DD');
};

/** Alterna o modo sem perder a data/prazo anteriores (memória de UI). */
export const applyEntregavelAgendamentoMode = (item = {}, mode) => {
  if (mode === ENTREGAVEL_AGENDAMENTO_DATA_FIXA) {
    const restoredDtPrev = hasEntregavelDtPrev(item)
      ? item.contrFornEvento_dtPrev
      : (item._dtPrevRemembered || null);
    const prazoAtual = getEntregavelPrazoDias(item);

    return {
      ...item,
      agendamentoMode: ENTREGAVEL_AGENDAMENTO_DATA_FIXA,
      contrFornEvento_dtPrev: restoredDtPrev,
      contrFornEvento_pgtoPrazoDias: null,
      _dtPrevRemembered: restoredDtPrev || item._dtPrevRemembered || null,
      _prazoRemembered: prazoAtual ?? item._prazoRemembered ?? null,
    };
  }

  const dtPrevAtual = hasEntregavelDtPrev(item)
    ? item.contrFornEvento_dtPrev
    : null;
  const rememberedDtPrev = dtPrevAtual || item._dtPrevRemembered || null;
  const restoredPrazo = getEntregavelPrazoDias(item) ?? item._prazoRemembered ?? null;

  return {
    ...item,
    agendamentoMode: ENTREGAVEL_AGENDAMENTO_PRAZO,
    contrFornEvento_dtPrev: null,
    contrFornEvento_pgtoPrazoDias: restoredPrazo,
    _dtPrevRemembered: rememberedDtPrev,
    _prazoRemembered: restoredPrazo,
  };
};

/** Garante exclusividade dos campos conforme o modo (para persistência / UI). */
export const normalizeEntregavelAgendamentoFields = (item = {}) => {
  const mode = getEntregavelAgendamentoMode(item);
  if (mode === ENTREGAVEL_AGENDAMENTO_DATA_FIXA) {
    const dtPrev = hasEntregavelDtPrev(item)
      ? item.contrFornEvento_dtPrev
      : (item._dtPrevRemembered || null);
    return {
      ...item,
      agendamentoMode: ENTREGAVEL_AGENDAMENTO_DATA_FIXA,
      contrFornEvento_dtPrev: dtPrev,
      contrFornEvento_pgtoPrazoDias: null,
      _dtPrevRemembered: dtPrev || item._dtPrevRemembered || null,
    };
  }

  const prazo = getEntregavelPrazoDias(item);
  const rememberedDtPrev = hasEntregavelDtPrev(item)
    ? item.contrFornEvento_dtPrev
    : (item._dtPrevRemembered || null);

  return {
    ...item,
    agendamentoMode: ENTREGAVEL_AGENDAMENTO_PRAZO,
    contrFornEvento_dtPrev: null,
    contrFornEvento_pgtoPrazoDias: prazo != null && prazo >= 0 ? prazo : null,
    _dtPrevRemembered: rememberedDtPrev,
    _prazoRemembered: (prazo != null && prazo >= 0 ? prazo : null) ?? item._prazoRemembered ?? null,
  };
};

export const isEntregavelAgendamentoCompleto = (item = {}) => {
  const mode = getEntregavelAgendamentoMode(item);
  if (mode === ENTREGAVEL_AGENDAMENTO_DATA_FIXA) {
    return hasEntregavelDtPrev(item);
  }
  const prazo = getEntregavelPrazoDias(item);
  return prazo != null && prazo >= 0;
};

export const buildEntregavelParcela = (
  {
    descricao,
    prazoDias,
    valorPct,
    valorVlr,
    valueSource,
    dtPrev,
    agendamentoMode,
  } = {},
  index,
  valorGlobal,
  existingParcela = {},
) => {
  const source = valueSource
    || existingParcela._valueSource
    || 'pct';

  let pct;
  let vlr;
  if (source === 'vlr') {
    vlr = round2(parseMoneyValue(valorVlr) ?? existingParcela.contrFornEvento_vlr ?? 0);
    pct = calcularPctEntregavel(valorGlobal, vlr);
  } else {
    pct = parsePercentValue(valorPct) ?? parsePercentValue(existingParcela.valorPct) ?? 0;
    vlr = calcularValorEntregavel(valorGlobal, pct);
  }

  const candidate = {
    ...existingParcela,
    contrFornEvento_pgtoPrazoDias: prazoDias !== undefined
      ? prazoDias
      : existingParcela.contrFornEvento_pgtoPrazoDias,
    contrFornEvento_dtPrev: dtPrev !== undefined
      ? dtPrev
      : existingParcela.contrFornEvento_dtPrev,
  };

  const mode = agendamentoMode || getEntregavelAgendamentoMode(candidate);

  const base = {
    ...existingParcela,
    contrFornEvento_num: index + 1,
    // Não usar trim aqui: updateEntregavel/renumber chamam isto a cada tecla
    // e trim() remove o espaço final, impedindo digitar palavras compostas.
    contrFornEvento_desc: descricao == null ? '' : String(descricao),
    contrFornEvento_vlr: vlr,
    valorPct: pct,
    contrFornEvento_obs: buildEntregavelObs(pct),
    _valueSource: source,
  };

  if (mode === ENTREGAVEL_AGENDAMENTO_DATA_FIXA) {
    const dtPrevValue = candidate.contrFornEvento_dtPrev
      || existingParcela._dtPrevRemembered
      || null;
    return {
      ...base,
      agendamentoMode: ENTREGAVEL_AGENDAMENTO_DATA_FIXA,
      contrFornEvento_dtPrev: dtPrevValue,
      contrFornEvento_pgtoPrazoDias: null,
      _dtPrevRemembered: dtPrevValue || existingParcela._dtPrevRemembered || null,
      _prazoRemembered: existingParcela._prazoRemembered ?? null,
    };
  }

  const prazo = parseIntegerValue(candidate.contrFornEvento_pgtoPrazoDias);
  const rememberedDtPrev = hasEntregavelDtPrev(existingParcela)
    ? existingParcela.contrFornEvento_dtPrev
    : (existingParcela._dtPrevRemembered || null);

  return {
    ...base,
    agendamentoMode: ENTREGAVEL_AGENDAMENTO_PRAZO,
    contrFornEvento_pgtoPrazoDias: prazo,
    contrFornEvento_dtPrev: null,
    _dtPrevRemembered: rememberedDtPrev,
    _prazoRemembered: (prazo != null && prazo >= 0 ? prazo : null) ?? existingParcela._prazoRemembered ?? null,
  };
};

export const getSomaValorEntregaveis = (entregaveis = []) => (
  round2((entregaveis || []).reduce(
    (acc, item) => acc + Number(item.contrFornEvento_vlr || 0),
    0,
  ))
);

export const entregaveisTotalBateComValorGlobal = (entregaveis = [], valorGlobal = 0) => (
  getSomaValorEntregaveis(entregaveis) === round2(Number(valorGlobal || 0))
);

export const aplicarValoresDistribuidosEntregaveis = (entregaveis = [], valorGlobal = 0) => {
  const base = round2(Number(valorGlobal || 0));
  if (!entregaveis.length || getSomaPercentualEntregaveis(entregaveis) !== 100 || !base) {
    return entregaveis;
  }

  if (entregaveis.length === 1) {
    return [applyValorAndPct(entregaveis[0], 100, base)];
  }

  const next = entregaveis.map((item, index) => {
    if (index === entregaveis.length - 1) return item;

    const vlr = calcularValorEntregavel(base, getValorPctFromEntregavel(item));
    return applyValorAndPct(item, getValorPctFromEntregavel(item), vlr);
  });

  const sumExceptLast = round2(
    next.slice(0, -1).reduce((acc, item) => acc + Number(item.contrFornEvento_vlr || 0), 0),
  );
  const lastIndex = next.length - 1;
  const lastVlr = round2(base - sumExceptLast);
  const lastPct = calcularPctEntregavel(base, lastVlr);

  next[lastIndex] = applyValorAndPct(next[lastIndex], lastPct, lastVlr);

  return next;
};

export const renumberEntregaveis = (entregaveis = [], valorGlobal = 0, options = {}) => {
  const rebuilt = (entregaveis || []).map((item, index) => buildEntregavelParcela({
    descricao: item.contrFornEvento_desc,
    prazoDias: item.contrFornEvento_pgtoPrazoDias,
    dtPrev: item.contrFornEvento_dtPrev,
    valorPct: getValorPctFromEntregavel(item),
    valorVlr: item.contrFornEvento_vlr,
    valueSource: item._valueSource,
    agendamentoMode: getEntregavelAgendamentoMode(item),
  }, index, valorGlobal, item));

  const hasVlrSource = (entregaveis || []).some((item) => item._valueSource === 'vlr');
  const shouldRedistribute = options.redistributeCents !== false && !hasVlrSource;

  const next = shouldRedistribute
    ? aplicarValoresDistribuidosEntregaveis(rebuilt, valorGlobal)
    : rebuilt;

  return next.map(normalizeEntregavelAgendamentoFields);
};

export const hydrateEntregavelFromParcela = (parcela = {}, valorGlobal = 0) => {
  const obs = String(parcela.contrFornEvento_obs || '');
  const match = obs.match(/ENTREGAVEL_PCT:([\d.,]+)/);
  let valorPct = match ? parsePercentValue(match[1]) : null;

  if (valorPct == null) {
    const base = Number(valorGlobal || 0);
    const vlr = Number(parcela.contrFornEvento_vlr || 0);
    if (base > 0 && vlr > 0) {
      valorPct = round2((vlr / base) * 100);
    }
  }

  const hydrated = {
    ...parcela,
    valorPct: valorPct ?? '',
    contrFornEvento_obs: valorPct != null && valorPct !== ''
      ? buildEntregavelObs(valorPct)
      : parcela.contrFornEvento_obs,
  };

  // Inferir modo a partir dos campos do banco (sem agendamentoMode explícito ainda).
  const inferred = { ...hydrated };
  delete inferred.agendamentoMode;
  const mode = getEntregavelAgendamentoMode(inferred);
  const withMode = {
    ...hydrated,
    agendamentoMode: mode,
    _dtPrevRemembered: hasEntregavelDtPrev(hydrated)
      ? hydrated.contrFornEvento_dtPrev
      : null,
    _prazoRemembered: getEntregavelPrazoDias(hydrated),
  };

  return normalizeEntregavelAgendamentoFields(withMode);
};

export const hydrateEntregaveisFromParcelas = (parcelas = [], valorGlobal = 0) => (
  renumberEntregaveis(
    (parcelas || []).map((parcela) => hydrateEntregavelFromParcela(parcela, valorGlobal)),
    valorGlobal,
  )
);

export const validarEntregavelDraft = (draft = {}, somaAtualOrOptions = 0, valorGlobalArg = 0) => {
  const options = typeof somaAtualOrOptions === 'object' && somaAtualOrOptions != null
    ? somaAtualOrOptions
    : { somaValorAtual: undefined, somaPercentualAtual: somaAtualOrOptions, valorGlobal: valorGlobalArg };
  const valorGlobal = Number(options.valorGlobal || 0);
  const somaValorAtual = options.somaValorAtual != null
    ? Number(options.somaValorAtual)
    : 0;

  const descricao = String(draft.descricao || '').trim();
  const valorPct = parsePercentValue(draft.valorPct);
  const valorVlr = parseMoneyValue(draft.valorVlr)
    ?? (valorPct != null ? calcularValorEntregavel(valorGlobal, valorPct) : null);
  const mode = draft.agendamentoMode === ENTREGAVEL_AGENDAMENTO_DATA_FIXA
    ? ENTREGAVEL_AGENDAMENTO_DATA_FIXA
    : ENTREGAVEL_AGENDAMENTO_PRAZO;

  if (!descricao) return 'Informe a descrição do entregável.';
  if (mode === ENTREGAVEL_AGENDAMENTO_DATA_FIXA) {
    if (!hasEntregavelDtPrev({ contrFornEvento_dtPrev: draft.dtPrev })) {
      return 'Informe a data prevista do entregável.';
    }
  } else {
    const prazoDias = parseIntegerValue(draft.prazoDias);
    if (prazoDias == null || prazoDias < 0) return 'Informe um prazo em dias válido.';
  }
  if (valorVlr == null || valorVlr <= 0) return 'Informe um valor maior que zero.';
  if (valorGlobal > 0 && round2(somaValorAtual + valorVlr) > round2(valorGlobal)) {
    return 'A soma dos valores não pode ultrapassar o valor da proposta.';
  }

  return null;
};

export const isEntregavelDraftVazio = (draft = {}) => {
  const descricao = String(draft.descricao || '').trim();
  const valorPct = parsePercentValue(draft.valorPct);
  const valorVlr = parseMoneyValue(draft.valorVlr);
  const hasDtPrev = hasEntregavelDtPrev({ contrFornEvento_dtPrev: draft.dtPrev });
  const prazo = parseIntegerValue(draft.prazoDias);
  return !descricao && valorPct == null && valorVlr == null && !hasDtPrev && prazo == null;
};

export const isEntregavelDraftComValor = (draft = {}, valorGlobal = 0) => {
  const valorPct = parsePercentValue(draft.valorPct);
  const valorVlr = parseMoneyValue(draft.valorVlr)
    ?? (valorPct != null ? calcularValorEntregavel(valorGlobal, valorPct) : null);
  return valorVlr != null && valorVlr > 0;
};

export const buildEntregavelParcelaFromDraft = (draft = {}, index, valorGlobal) => (
  buildEntregavelParcela({
    descricao: draft.descricao,
    prazoDias: draft.prazoDias,
    dtPrev: draft.dtPrev,
    valorPct: draft.valorPct,
    valorVlr: draft.valorVlr,
    valueSource: draft.valueSource || 'pct',
    agendamentoMode: draft.agendamentoMode || ENTREGAVEL_AGENDAMENTO_PRAZO,
  }, index, valorGlobal, {
    _dtPrevRemembered: draft.dtPrev || draft.dtPrevRemembered || null,
    _prazoRemembered: (draft.prazoDias !== '' && draft.prazoDias != null)
      ? draft.prazoDias
      : (draft.prazoRemembered ?? null),
    _valueSource: draft.valueSource || 'pct',
    _isDraft: true,
  })
);

export const splitValorRestanteEmN = (restante, n) => {
  const quantidade = Number.parseInt(String(n), 10);
  if (!Number.isFinite(quantidade) || quantidade <= 0) return [];
  const totalCents = Math.round(Number(restante || 0) * 100);
  if (totalCents <= 0) return [];
  const baseCents = Math.floor(totalCents / quantidade);
  const remainder = totalCents - baseCents * quantidade;
  return Array.from({ length: quantidade }, (_, index) => (
    (index === quantidade - 1 ? baseCents + remainder : baseCents) / 100
  ));
};

export const buildEntregaveisEmLote = ({
  quantidade,
  descricaoPadrao = '',
  restante,
  valorGlobal,
  startIndex = 0,
} = {}) => {
  const valores = splitValorRestanteEmN(restante, quantidade);
  const prefixo = String(descricaoPadrao || '').trim() || 'Entregável';
  return valores.map((valorVlr, index) => buildEntregavelParcela({
    descricao: `${prefixo} ${startIndex + index + 1}`,
    prazoDias: 0,
    valorVlr,
    valueSource: 'vlr',
    agendamentoMode: ENTREGAVEL_AGENDAMENTO_PRAZO,
  }, startIndex + index, valorGlobal, {
    _valueSource: 'vlr',
  }));
};

export const validarEntregaveisParaSubmit = (entregaveis = [], valorGlobal = null) => {
  if (!Array.isArray(entregaveis) || entregaveis.length === 0) {
    return 'Cadastre pelo menos um entregável.';
  }

  for (let index = 0; index < entregaveis.length; index += 1) {
    const item = entregaveis[index];
    const descricao = String(item.contrFornEvento_desc || '').trim();
    const mode = getEntregavelAgendamentoMode(item);

    if (!descricao) {
      return `O entregável ${index + 1} precisa de descrição.`;
    }
    if (!hasEntregavelDtPrev(item) && !isPrazoDiasInformado(item.contrFornEvento_pgtoPrazoDias)) {
      return `O entregável ${index + 1} não possui data prevista nem prazo para pagamento.`;
    }
    if (mode === ENTREGAVEL_AGENDAMENTO_DATA_FIXA) {
      if (!hasEntregavelDtPrev(item)) {
        return `O entregável ${index + 1} precisa de data prevista.`;
      }
    } else {
      const prazoDias = getEntregavelPrazoDias(item);
      if (prazoDias == null || prazoDias < 0) {
        return `O entregável ${index + 1} precisa de prazo em dias válido.`;
      }
    }
    const vlr = round2(Number(item.contrFornEvento_vlr || 0));
    if (!(vlr > 0)) {
      return `O entregável ${index + 1} precisa de um valor maior que zero.`;
    }
  }

  if (valorGlobal != null && !entregaveisTotalBateComValorGlobal(entregaveis, valorGlobal)) {
    return 'A soma dos valores dos entregáveis deve ser igual ao valor da proposta.';
  }

  return null;
};

export const isEntregaveisSumComplete = (entregaveis = [], valorGlobal = null) => (
  (entregaveis || []).every((item) => round2(Number(item.contrFornEvento_vlr || 0)) > 0)
  && (valorGlobal == null || entregaveisTotalBateComValorGlobal(entregaveis, valorGlobal))
  && (entregaveis || []).every(isEntregavelAgendamentoCompleto)
);

/**
 * Data prevista do entregável = data de assinatura do contrato + prazo em dias.
 * Retorna YYYY-MM-DD ou null se inputs inválidos.
 * Aceita prazo 0 (mesma data da assinatura). Campo vazio não calcula.
 */
export const calcDtPrevFromAssinatura = (dtAssin, prazoDias) => {
  if (!dtAssin || prazoDias == null || prazoDias === '') return null;
  const dias = Number.parseInt(String(prazoDias), 10);
  if (!Number.isFinite(dias) || dias < 0) return null;
  const base = moment(dtAssin, 'YYYY-MM-DD', true);
  if (!base.isValid()) return null;
  return base.clone().add(dias, 'days').format('YYYY-MM-DD');
};
